import { useMutation, useQueryClient } from '@tanstack/react-query';
import { axios } from '@/lib/axios/axios';
import { MESSAGING_API_BASE, messagingQueryKeys } from '../config';
import { ChannelResponse } from '../types/backend';
import { updateChannelInCache } from '../utils/updateChannelCache';

export type RenameChannelDTO = {
  channelId: number;
  name: string;
  version: number;
};

const renameChannel = async (
  data: RenameChannelDTO
): Promise<ChannelResponse> => {
  const { channelId, name, version } = data;

  return axios.put(`${MESSAGING_API_BASE}/channels/${channelId}/rename`, {
    channelId,
    name,
    version,
  });
};

export const useRenameChannel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RenameChannelDTO) => renameChannel(data),
    onSuccess: (response, variables) => {
      // Update channel name in cache without triggering refetch
      // This preserves the channel's position (doesn't re-sort)
      updateChannelInCache(queryClient, variables.channelId, {
        name: variables.name,
      });

      // Update channel details cache with response (no refetch) to avoid
      // duplicate calls: we already have the updated channel payload.
      queryClient.setQueryData(
        messagingQueryKeys.channel(variables.channelId),
        response as ChannelResponse
      );

      // System message arrives via MESSAGING_NEW_MESSAGE socket event
      // No need to invalidate - handleNewMessage adds it to cache
    },
  });
};
